import { idleCallbackPromise } from '@/utils/helpers'

type BatchConfig = {
    enabled: boolean
    flushIntervalMs: number
    maxBatchSizeBytes: number
    maxBatchSizeCount: number
}

export class BatchQueue<T> {
    private queue: string[] = []
    private queueSizeBytes: number = 0
    private flushTimer: ReturnType<typeof setTimeout> | null = null

    constructor(
        private url: string,
        private config: BatchConfig,
    ) {}

    add(event: T): void {
        const eventString = JSON.stringify(event)
        const eventSize = eventString.length * 2

        if (!this.config.enabled) {
            this.sendBatch([eventString])
            return
        }

        if (this.queueSizeBytes + eventSize > this.config.maxBatchSizeBytes) {
            this.flush()
        }

        if (this.queue.length >= this.config.maxBatchSizeCount) {
            this.flush()
        }

        this.queue.push(eventString)
        this.queueSizeBytes += eventSize
        this.scheduleFlush()
    }

    flush(): void {
        if (this.flushTimer) {
            clearTimeout(this.flushTimer)
            this.flushTimer = null
        }

        if (this.queue.length === 0) {
            return
        }

        const eventsToSend = [...this.queue]
        this.queue = []
        this.queueSizeBytes = 0

        this.sendBatch(eventsToSend)
    }

    destroy(): void {
        if (this.flushTimer) {
            clearTimeout(this.flushTimer)
            this.flushTimer = null
        }
        if (this.queue.length > 0) {
            this.flush()
        }
    }

    private scheduleFlush(): void {
        if (this.flushTimer !== null) {
            return
        }

        this.flushTimer = setTimeout(async () => {
            this.flushTimer = null
            await idleCallbackPromise()
            this.flush()
        }, this.config.flushIntervalMs)
    }

    private sendBatch(events: string[]): void {
        try {
            const body = `{"events":[${events.join(',')}]}`
            const blob = new Blob([body], { type: 'application/json' })
            const success = navigator.sendBeacon(this.url, blob)

            if (!success) {
                // eslint-disable-next-line no-console
                console.warn('[Analytics] sendBeacon rejected batch', {
                    eventCount: events.length,
                    sizeBytes: body.length,
                    maxBytes: this.config.maxBatchSizeBytes,
                })
            }
        } catch (e) {
            console.error('[Analytics] Failed to send batch', {
                eventCount: events.length,
                error: e,
            })
        }
    }
}
